import React, {useRef, useState} from 'react';
import Modal from "react-modal";
import {Button, FormControlLabel, Radio, RadioGroup, Switch, ThemeProvider} from "@mui/material";
import {buttonTheme, switchTheme} from "../utils/muiStyling";
import {MuiCloseIconButton} from "./muiComponents";
import {sendAdobeQuote, sendAdobeQuoteRaw, uploadAdobeQuoteTemplate, getLatestCurrencyConversionRate} from "../pages/api/adobeQuoteApi";
import NotificationManager from "react-notifications/lib/NotificationManager";

const DEAL_LENGTHS = [
    { value: "OneYear",    label: "1 Year" },
    { value: "TwoYears",   label: "2 Years" },
    { value: "ThreeYears", label: "3 Years" },
];

const MAX_PRODUCTS = 4;

// Expandable list of supported currency codes
const CURRENCIES = [
    { code: "USD", label: "USD — US Dollar" },
    { code: "GBP", label: "GBP — British Pound" },
    { code: "AED", label: "AED — UAE Dirham" },
];

// Applies a percentage increase and rounds to 2 decimal places
const applyIncrease = (rate, pct) => Math.round(rate * (1 + pct / 100) * 100) / 100;

const PRODUCTS = [
    { name: "CyberCrucible Core Desktop",      description: "Cyber Crucible ransomware, data, and identity prevention",                                    rate: 75,  monthlyRate: 6.5   },
    { name: "CyberCrucible Core Server",        description: "Cyber Crucible ransomware, data, and identity prevention",                                    rate: 150, monthlyRate: 13.0  },
    { name: "Fortress AI Lite",                 description: "AI Acceptable Use Lite Edition",                                                              rate: 30,  monthlyRate: 2.75  },
    { name: "CC Core + FAI Lite Desktop",       description: "Cyber Crucible ransomware, data, and identity prevention and AI Acceptable Use Lite Edition", rate: 100, monthlyRate: 8.75  },
    { name: "CC Core + FAI Lite Server",        description: "Cyber Crucible ransomware, data, and identity prevention and AI Acceptable Use Lite Edition", rate: 180, monthlyRate: 15.75 },
];

const STATIC_FIELD_GROUPS = [
    {
        label: "Quote Info",
        fields: [
            { key: "EstimateNumber", label: "Estimate Number", required: true },
            { key: "Currency",       label: "Currency", required: true, isCurrencySelect: true },
            { key: "CCSalesRep",     label: "CC Sales Rep", required: true },
        ],
    },
    {
        label: "Client",
        fields: [
            { key: "ClientBusinessName",  label: "Business Name", required: true },
            { key: "ClientBusinessEmail", label: "Business Email", requiredEither: true },
            { key: "ClientBusinessPhone", label: "Business Phone", requiredEither: true },
            { key: "ResaleToName",        label: "Resale To Name" },
            { key: "ClientNotes",         label: "Client Notes", multiline: true },
        ],
    },
];

const productFields = (n) => [
    { key: `ProductService${n}`,            label: "Product / Service", required: true, isProductSelect: true },
    { key: `ProductServiceDescription${n}`, label: "Description",                       isProductDesc: true },
    { key: `Quantity${n}`,                  label: "Quantity", required: true },
    { key: `RatePerYear${n}`,               label: "Rate / Year (2026)", isRateField: true },
    { key: `RatePerYear${n}2027`,           label: "Rate / Year (2027)", isHidden: true },
    { key: `RatePerYear${n}2028`,           label: "Rate / Year (2028)", isHidden: true },
];

const ALL_STATIC_KEYS = STATIC_FIELD_GROUPS.flatMap(g => g.fields.map(f => f.key));
const ALL_PRODUCT_KEYS = [1, 2, 3, 4].flatMap(n => [...productFields(n).map(f => f.key), `RatePerMonth${n}`]);
const buildEmptyFields = () => Object.fromEntries([...ALL_STATIC_KEYS, ...ALL_PRODUCT_KEYS].map(k => [k, ""]));

const generateEstimateNumber = () => {
    const now = new Date();
    const pad = n => String(n).padStart(2, "0");
    return `EST-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${String(now.getTime()).slice(-4)}`;
};

export default function AdobeQuoteModal({ isOpen, setIsOpen, setIsLoading }) {
    const [useFields, setUseFields] = useState(true);
    const [fieldValues, setFieldValues] = useState(() => ({
        ...buildEmptyFields(),
        EstimateNumber: generateEstimateNumber(),
        Currency: "USD",
    }));
    const [jsonText, setJsonText] = useState("");
    const [adobeQuoteFile, setAdobeQuoteFile] = useState(null);
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [currencyRates, setCurrencyRates] = useState({}); // rates map from DB snapshot
    const [productCount, setProductCount] = useState(1);
    const [activeTab, setActiveTab] = useState("quote"); // "quote" | "templates"

    // Template upload state
    const [templateScenario, setTemplateScenario] = useState("Scenario1");
    const [templateDocxFile, setTemplateDocxFile] = useState(null);
    const [templateHtmlFile, setTemplateHtmlFile] = useState(null);
    const [templateUploading, setTemplateUploading] = useState(false);
    const [templateRows, setTemplateRows] = useState(1);
    const templateDocxRef = useRef(null);
    const templateHtmlRef = useRef(null);

    // Deal structure
    const [dealLength, setDealLength] = useState("OneYear");
    const [paidUpfront, setPaidUpfront] = useState(true);
    const [billingCycle, setBillingCycle] = useState("upfront");
    const [startDate, setStartDate] = useState("");

    // Per-slot custom discounts (null = use auto-derived)
    const [customVolumeDiscount, setCustomVolumeDiscount] = useState("");
    const [customDealDiscount, setCustomDealDiscount] = useState("");
    const [customAdditionalDiscount, setCustomAdditionalDiscount] = useState("");
    const [customAdditionalDiscountLabel, setCustomAdditionalDiscountLabel] = useState("");

    function resetModal() {
        setIsOpen(false);
        setUseFields(true);
        setFieldValues({ ...buildEmptyFields(), EstimateNumber: generateEstimateNumber(), Currency: "USD" });
        setJsonText("");
        setAdobeQuoteFile(null);
        setButtonDisabled(false);
        setIsLoading(false);
        setProductCount(1);
        setCurrencyRates({});
        setActiveTab("quote");
        setTemplateScenario("Scenario1");
        setTemplateDocxFile(null);
        setTemplateHtmlFile(null);
        setTemplateUploading(false);
        setTemplateRows(1);
        if (templateDocxRef.current) templateDocxRef.current.value = "";
        if (templateHtmlRef.current) templateHtmlRef.current.value = "";
        setDealLength("OneYear");
        setPaidUpfront(true);
        setBillingCycle("upfront");
        setStartDate("");
        setCustomVolumeDiscount("");
        setCustomDealDiscount("");
        setCustomAdditionalDiscount("");
        setCustomAdditionalDiscountLabel("");
    }

    function resetTemplateFiles() {
        setTemplateDocxFile(null);
        setTemplateHtmlFile(null);
        if (templateDocxRef.current) templateDocxRef.current.value = "";
        if (templateHtmlRef.current) templateHtmlRef.current.value = "";
    }

    function handleFieldChange(key, value) {
        setFieldValues(prev => {
            const next = { ...prev, [key]: value };

            // If a RatePerYear field changed, recompute the 2027/2028 rates dynamically
            const rateMatch = key.match(/^RatePerYear(\d+)$/);
            if (rateMatch) {
                const n = rateMatch[1];
                const rateNum = parseFloat(value.replace(/[^0-9.]/g, ""));
                if (!isNaN(rateNum) && rateNum > 0) {
                    const r2027 = applyIncrease(rateNum, 7);
                    const r2028 = applyIncrease(r2027, 7);
                    next[`RatePerYear${n}2027`] = String(r2027);
                    next[`RatePerYear${n}2028`] = String(r2028);
                } else {
                    next[`RatePerYear${n}2027`] = "";
                    next[`RatePerYear${n}2028`] = "";
                }
            }

            return next;
        });
    }

    function handleCurrencyChange(key, val) {
        handleFieldChange(key, val);
        if (val && val !== "USD") {
            getLatestCurrencyConversionRate()
                .then(res => {
                    if (res.data?.rates) setCurrencyRates(res.data.rates);
                })
                .catch(() => {
                    NotificationManager.error("Failed to fetch currency conversion rates");
                    setCurrencyRates({});
                });
        } else {
            setCurrencyRates({});
        }
    }

    function parseDiscount(val) {
        if (val === "" || val === null || val === undefined) return null;
        const n = parseFloat(val);
        return isNaN(n) ? null : n;
    }

    function validateFields() {
        const v = fieldValues;

        if (!v.EstimateNumber?.trim()) return "Estimate Number is required";
        if (!v.Currency?.trim()) return "Currency is required — please select a currency";
        if (!v.CCSalesRep?.trim()) return "CC Sales Rep is required";
        if (!startDate?.trim()) return "Start Date is required";
        if (!v.ClientBusinessName?.trim()) return "Client Business Name is required";
        if (!v.ClientBusinessEmail?.trim() && !v.ClientBusinessPhone?.trim())
            return "Client Business Email or Phone is required";

        const activeProducts = Array.from({ length: productCount }, (_, i) => i + 1);
        for (const n of activeProducts) {
            const svc = v[`ProductService${n}`]?.trim();
            const qty = v[`Quantity${n}`]?.trim();
            if (!svc) return `Product Service ${n}: Product / Service name is required`;
            if (!qty) return `Product Service ${n}: Quantity is required`;
            const qtyNum = Number(qty.replace(/,/g, ""));
            if (!Number.isInteger(qtyNum) || qtyNum <= 0)
                return `Product Service ${n}: Quantity must be a positive whole number`;
        }

        const cvd = parseDiscount(customVolumeDiscount);
        if (cvd !== null && (cvd < 0 || cvd > 100))
            return "Custom Volume Discount must be between 0 and 100";

        const cdd = parseDiscount(customDealDiscount);
        if (cdd !== null && (cdd < 0 || cdd > 100))
            return "Custom Deal Discount must be between 0 and 100";

        const cad = parseDiscount(customAdditionalDiscount);
        if (cad !== null && (cad < 0 || cad > 100))
            return "Custom Additional Discount must be between 0 and 100";

        return null;
    }

    function buildJsonObject() {
        if (useFields) {
            const error = validateFields();
            if (error) return { ok: false, error };
            const v = fieldValues;

            const buildProduct = (n) => {
                const svc = v[`ProductService${n}`]?.trim();
                const qty = v[`Quantity${n}`]?.trim();
                if (!svc && !qty) return undefined;
                const qtyNum = qty ? parseInt(qty.replace(/,/g, ""), 10) : null;
                return {
                    productService: svc || "",
                    productServiceDescription: v[`ProductServiceDescription${n}`]?.trim() || "",
                    quantity: (qtyNum != null && !isNaN(qtyNum)) ? qtyNum : null,
                    // rates intentionally omitted — owned by backend constants
                };
            };

            const obj = {
                dealLength,
                paidUpfront,
                billingCycle,
                startDate,
                customVolumeDiscount:          parseDiscount(customVolumeDiscount),
                customDealDiscount:            parseDiscount(customDealDiscount),
                customAdditionalDiscount:      parseDiscount(customAdditionalDiscount),
                customAdditionalDiscountLabel: customAdditionalDiscountLabel?.trim() || null,
                estimateNumber: v.EstimateNumber || null,
                currency: v.Currency,
                ccSalesRep: v.CCSalesRep,
                clientBusinessName: v.ClientBusinessName,
                clientBusinessEmail: v.ClientBusinessEmail || null,
                clientBusinessPhone: v.ClientBusinessPhone || null,
                resaleToName: v.ResaleToName || null,
                clientNotes: v.ClientNotes || null,
                product1: buildProduct(1),
                product2: buildProduct(2),
                product3: buildProduct(3),
                product4: buildProduct(4),
            };
            return { ok: true, value: obj };
        }

        if (!jsonText.trim()) return { ok: false, error: "No JSON was entered" };
        try {
            const parsed = JSON.parse(jsonText);
            if (Object.keys(parsed).some(k => k === "")) return { ok: false, error: "JSON contains empty keys" };
            return { ok: true, value: parsed };
        } catch {
            return { ok: false, error: "Incorrect JSON format" };
        }
    }

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) setAdobeQuoteFile(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const jsonResult = buildJsonObject();
        if (!jsonResult.ok) {
            NotificationManager.error(jsonResult.error);
            return;
        }

        if (!useFields) {
            if (!adobeQuoteFile) { NotificationManager.error("No .docx file selected"); return; }
            if (!adobeQuoteFile.name.endsWith('.docx')) { NotificationManager.error("File must be a .docx"); return; }
            if (adobeQuoteFile.size === 0) { NotificationManager.error("File is empty"); return; }
        }

        setIsLoading(true);
        setButtonDisabled(true);

        const request = useFields
            ? sendAdobeQuote(jsonResult.value)
            : sendAdobeQuoteRaw(jsonResult.value, adobeQuoteFile);

        request
            .then(() => {
                resetModal();
                NotificationManager.success("Adobe Quote Sent");
            })
            .catch(error => {
                NotificationManager.error(error.message || "Unexpected error generating quote.");
                setIsLoading(false);
                setButtonDisabled(false);
            });
    };

    // Label for the deal discount field depending on current scenario
    const dealDiscountLabel = () => {
        if (billingCycle === "monthly") return "Deal Length Discount % (Monthly)";
        if (!paidUpfront && dealLength === "TwoYears") return "Deal Length Discount % (2yr Annual)";
        if (!paidUpfront && dealLength === "ThreeYears") return "Deal Length Discount % (3yr Annual)";
        if (!paidUpfront) return "Deal Length Discount % (Annual)";
        if (dealLength === "TwoYears") return "Deal Length Discount % (2yr Upfront)";
        if (dealLength === "ThreeYears") return "Deal Length Discount % (3yr Upfront)";
        return "Deal Length Discount % (1yr Upfront)";
    };

    return (
        <Modal
            contentLabel="Adobe Quote Testing"
            isOpen={isOpen}
            onRequestClose={resetModal}
            shouldCloseOnOverlayClick={true}
            className="focus:outline-none focus:shadow-sm border-2 flex relative z-50 bg-white max-w-2xl inset-y-10 mx-auto rounded-2xl"
            overlayClassName="z-50 bg-black bg-opacity-5 fixed inset-0 overflow-scroll"
        >
            <div className="flex flex-1 flex-col p-8 w-full ml-4 mr-4 gap-y-4">

                {/* Header */}
                <div className="flex flex-row justify-between items-center">
                    <h1 className="font-bold text-3xl">Adobe Quote</h1>
                    <MuiCloseIconButton onClick={resetModal} />
                </div>
                <hr className="h-0.5" />

                {/* Tab bar */}
                <div className="flex flex-row border-b border-gray-200">
                    {[
                        { key: "quote",     label: "Generate Quote" },
                        { key: "templates", label: "Upload Templates" },
                    ].map(({ key, label }) => (
                        <button
                            key={key}
                            type="button"
                            onClick={() => setActiveTab(key)}
                            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                                activeTab === key
                                    ? "border-blue-600 text-blue-600"
                                    : "border-transparent text-slate-500 hover:text-slate-700"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {activeTab === "templates" ? (
                    <div className="flex flex-col gap-y-4">
                        <p className="text-xs text-slate-500">
                            Upload a new <code>.docx</code> template for a scenario. The most recent upload per scenario is used when generating quotes.
                        </p>
                        <div className="flex flex-col gap-y-1">
                            <label className="text-xs text-slate-500">Scenario</label>
                            <select
                                value={templateScenario}
                                onChange={e => setTemplateScenario(e.target.value)}
                                className="focus:outline-none h-9 px-2 rounded-lg border border-black border-opacity-25 text-sm bg-white"
                            >
                                {[
                                    { value: "Scenario1", label: "Scenario 1 — 1 Year, Monthly" },
                                    { value: "Scenario2", label: "Scenario 2 — 1 Year, Paid Upfront" },
                                    { value: "Scenario3", label: "Scenario 3 — 2 Year, Annually" },
                                    { value: "Scenario4", label: "Scenario 4 — 2 Year, Paid Upfront" },
                                    { value: "Scenario5", label: "Scenario 5 — 3 Year, Annually" },
                                    { value: "Scenario6", label: "Scenario 6 — 3 Year, Paid Upfront" },
                                ].map(({ value, label }) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex flex-col gap-y-1">
                            <label className="text-xs text-slate-500">Product Rows</label>
                            <select
                                value={templateRows}
                                onChange={e => setTemplateRows(Number(e.target.value))}
                                className="focus:outline-none h-9 px-2 rounded-lg border border-black border-opacity-25 text-sm bg-white"
                            >
                                {[1, 2, 3, 4].map(n => (
                                    <option key={n} value={n}>{n} {n === 1 ? "row" : "rows"}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex flex-col gap-y-1">
                            <label className="text-xs text-slate-500">.docx template <span className="text-red-500">*</span></label>
                            <input
                                ref={templateDocxRef}
                                type="file"
                                accept=".docx"
                                onChange={e => setTemplateDocxFile(e.target.files?.[0] || null)}
                                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                            />
                            {templateDocxFile && <p className="text-xs text-gray-500">Selected: {templateDocxFile.name}</p>}
                        </div>
                        <div className="flex flex-col gap-y-1">
                            <label className="text-xs text-slate-500">.html preview <span className="text-red-500">*</span></label>
                            <input
                                ref={templateHtmlRef}
                                type="file"
                                accept=".html"
                                onChange={e => setTemplateHtmlFile(e.target.files?.[0] || null)}
                                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-slate-50 file:text-slate-700 hover:file:bg-slate-100 cursor-pointer"
                            />
                            {templateHtmlFile && <p className="text-xs text-gray-500">Selected: {templateHtmlFile.name}</p>}
                        </div>
                        <ThemeProvider theme={buttonTheme}>
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={templateUploading || !templateDocxFile || !templateHtmlFile}
                                onClick={async () => {
                                    if (!templateDocxFile) { NotificationManager.error("Please select a .docx file"); return; }
                                    if (!templateHtmlFile) { NotificationManager.error("Please select an .html file"); return; }
                                    setTemplateUploading(true);
                                    try {
                                        await uploadAdobeQuoteTemplate(templateScenario, templateRows, templateDocxFile, templateHtmlFile);
                                        NotificationManager.success("Template uploaded successfully");
                                        resetTemplateFiles();
                                    } catch (err) {
                                        NotificationManager.error(err.message || "Failed to upload template");
                                    } finally {
                                        setTemplateUploading(false);
                                        setTemplateRows(1);
                                    }
                                }}
                            >
                                {templateUploading ? "Uploading..." : "Upload Template"}
                            </Button>
                        </ThemeProvider>
                    </div>
                ) : (
                    <form className="flex flex-1 flex-col gap-y-4" onSubmit={handleSubmit}>
                        {/* Input mode toggle */}
                        <div className="flex flex-row items-center">
                            <ThemeProvider theme={switchTheme}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={useFields}
                                            onChange={e => setUseFields(e.target.checked)}
                                            name="inputModeToggle"
                                        />
                                    }
                                    label={useFields ? "Individual Fields" : "Raw JSON"}
                                />
                            </ThemeProvider>
                        </div>
                        <hr className="h-0.5" />

                        {useFields ? (
                            <>
                                {/* Deal structure controls */}
                                <div className="flex flex-col gap-y-2">
                                    <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Deal Structure</p>
                                    <div className="flex flex-row items-center gap-x-6 flex-wrap gap-y-2">

                                        <div className="flex flex-col gap-y-1">
                                            <label className="text-xs text-slate-500">Deal Length</label>
                                            <select
                                                value={dealLength}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setDealLength(val);
                                                    if (val === "OneYear") {
                                                        setPaidUpfront(true);
                                                        setBillingCycle("upfront");
                                                    } else {
                                                        if (billingCycle === "monthly") setBillingCycle("annual");
                                                    }
                                                }}
                                                className="focus:outline-none h-9 px-2 rounded-lg border border-black border-opacity-25 text-sm bg-white"
                                            >
                                                {DEAL_LENGTHS.map(({ value, label }) => (
                                                    <option key={value} value={value}>{label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="flex flex-col gap-y-1">
                                            <label className="text-xs text-slate-500">Billing</label>
                                            <RadioGroup
                                                row
                                                value={billingCycle}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setBillingCycle(val);
                                                    setPaidUpfront(val === "upfront");
                                                }}
                                            >
                                                {dealLength === "OneYear" ? (
                                                    <>
                                                        <FormControlLabel value="monthly" control={<Radio size="small" />} label={<span className="text-sm">Monthly</span>} />
                                                        <FormControlLabel value="upfront" control={<Radio size="small" />} label={<span className="text-sm">Paid Upfront</span>} />
                                                    </>
                                                ) : (
                                                    <>
                                                        <FormControlLabel value="annual"  control={<Radio size="small" />} label={<span className="text-sm">Annual</span>} />
                                                        <FormControlLabel value="upfront" control={<Radio size="small" />} label={<span className="text-sm">Paid Upfront</span>} />
                                                    </>
                                                )}
                                            </RadioGroup>
                                        </div>

                                        <div className="flex flex-col gap-y-1">
                                            <label className="text-xs text-slate-500">Service Start Date <span className="text-red-500" title="Required">*</span></label>
                                            <input
                                                type="date"
                                                value={startDate}
                                                onChange={e => setStartDate(e.target.value)}
                                                className="focus:outline-none h-9 px-2 rounded-lg border border-black border-opacity-25 text-sm bg-white"
                                            />
                                        </div>

                                    </div>
                                </div>
                                <hr className="h-0.5" />

                                {/* Custom Discounts */}
                                <div className="flex flex-col gap-y-2">
                                    <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Custom Discounts</p>
                                    <p className="text-xs text-slate-400">Leave blank to use standard group discounts. Enter 0 to explicitly remove a discount.</p>
                                    <div className="flex flex-col items-start gap-x-6 flex-wrap gap-y-2">
                                        <div className="flex flex-row gap-y-2 gap-x-10">
                                            <div className="flex flex-col gap-y-1">
                                                <label className="text-xs text-slate-500">Volume Discount %</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    step="0.01"
                                                    value={customVolumeDiscount}
                                                    onChange={e => setCustomVolumeDiscount(e.target.value)}
                                                    placeholder="auto"
                                                    className="focus:outline-none h-9 px-2 rounded-lg border border-black border-opacity-25 text-sm w-28"
                                                />
                                            </div>
                                            <div className="flex flex-col gap-y-1">
                                                <label className="text-xs text-slate-500">{dealDiscountLabel()}</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    step="0.01"
                                                    value={customDealDiscount}
                                                    onChange={e => setCustomDealDiscount(e.target.value)}
                                                    placeholder="auto"
                                                    className="focus:outline-none h-9 px-2 rounded-lg border border-black border-opacity-25 text-sm w-28"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-row gap-y-2 gap-x-6">
                                            <div className="flex flex-col gap-y-1">
                                                <label className="text-xs text-slate-500">Additional Discount %</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    step="0.01"
                                                    value={customAdditionalDiscount}
                                                    onChange={e => setCustomAdditionalDiscount(e.target.value)}
                                                    placeholder="none"
                                                    className="focus:outline-none h-9 px-2 rounded-lg border border-black border-opacity-25 text-sm w-28"
                                                />
                                            </div>
                                            <div className="flex flex-col gap-y-1">
                                                <label className="text-xs text-slate-500">Additional Discount Name</label>
                                                <input
                                                    type="text"
                                                    value={customAdditionalDiscountLabel}
                                                    onChange={e => setCustomAdditionalDiscountLabel(e.target.value)}
                                                    placeholder="e.g. Partner Discount"
                                                    className="focus:outline-none h-9 px-2 rounded-lg border border-black border-opacity-25 text-sm w-44"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <hr className="h-0.5" />

                                {/* Scrollable field area */}
                                <div className="flex flex-col gap-y-5 max-h-[40vh] overflow-y-auto pr-2">

                                    {/* Static field groups (Quote Info, Client) */}
                                    {STATIC_FIELD_GROUPS.map(group => (
                                        <div key={group.label} className="flex flex-col gap-y-2">
                                            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">{group.label}</p>
                                            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                                {group.fields.map(({ key, label, multiline, required, requiredEither, isCurrencySelect }) => (
                                                    <div key={key} className={`flex flex-col ${multiline ? "col-span-2" : ""}`}>
                                                        <label className="text-xs text-slate-500 mb-0.5">
                                                            {label}
                                                            {(required || requiredEither) && (
                                                                <span
                                                                    className="text-red-500 ml-0.5"
                                                                    title={requiredEither ? "Email or Phone required — at least one is required" : "Required"}
                                                                >*</span>
                                                            )}
                                                        </label>
                                                        {isCurrencySelect ? (
                                                            <>
                                                                <select
                                                                    value={fieldValues[key] ?? ""}
                                                                    onChange={e => handleCurrencyChange(key, e.target.value)}
                                                                    className="focus:outline-none h-9 px-2 rounded-lg border border-black border-opacity-25 text-sm bg-white"
                                                                >
                                                                    <option value="">Select currency...</option>
                                                                    {CURRENCIES.map(({ code, label }) => (
                                                                        <option key={code} value={code}>{label}</option>
                                                                    ))}
                                                                </select>
                                                            </>
                                                        ) : multiline ? (
                                                            <textarea
                                                                value={fieldValues[key] ?? ""}
                                                                onChange={e => handleFieldChange(key, e.target.value)}
                                                                placeholder={key}
                                                                rows={4}
                                                                className="focus:outline-none px-2 py-1.5 rounded-lg border border-black border-opacity-25 text-sm resize-y"
                                                            />
                                                        ) : (
                                                            <input
                                                                type="text"
                                                                value={fieldValues[key] ?? ""}
                                                                onChange={e => handleFieldChange(key, e.target.value)}
                                                                placeholder={key}
                                                                className="focus:outline-none h-9 px-2 rounded-lg border border-black border-opacity-25 text-sm"
                                                            />
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}

                                    {/* Dynamic product sections */}
                                    {Array.from({ length: productCount }, (_, i) => i + 1).map(n => (
                                        <div key={n} className="flex flex-col gap-y-2">
                                            <div className="flex flex-row items-center justify-between">
                                                <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                                                    Product / Service {n}
                                                </p>
                                                {n === productCount && n > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            productFields(n).forEach(({ key }) =>
                                                                handleFieldChange(key, "")
                                                            );
                                                            setProductCount(c => c - 1);
                                                        }}
                                                        className="text-xs text-red-500 hover:text-red-700 font-semibold"
                                                    >
                                                        − Remove
                                                    </button>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                                {productFields(n).filter(f => !f.isHidden).map(({ key, label, required, isProductSelect, isProductDesc, isRateField }) => (
                                                    <div key={key} className="flex flex-col">
                                                        <label className="text-xs text-slate-500 mb-0.5">
                                                            {isRateField && billingCycle === "monthly" ? "Rate / Month (2026)" : label}
                                                            {required && <span className="text-red-500 ml-0.5" title="Required">*</span>}
                                                        </label>
                                                        {isProductSelect ? (
                                                            <select
                                                                value={fieldValues[key] ?? ""}
                                                                onChange={e => {
                                                                    const val = e.target.value;
                                                                    const product = PRODUCTS.find(p => p.name === val);
                                                                    handleFieldChange(key, val);
                                                                    handleFieldChange(key.replace("ProductService", "ProductServiceDescription"), product?.description ?? val);
                                                                    if (product?.rate != null) handleFieldChange(key.replace("ProductService", "RatePerYear"), String(product.rate));
                                                                    if (product?.monthlyRate != null) handleFieldChange(key.replace("ProductService", "RatePerMonth"), String(product.monthlyRate));
                                                                }}
                                                                className="focus:outline-none h-9 px-2 rounded-lg border border-black border-opacity-25 text-sm bg-white"
                                                            >
                                                                <option value="">Select a product...</option>
                                                                {PRODUCTS.map(({ name }) => (
                                                                    <option key={name} value={name}>{name}</option>
                                                                ))}
                                                            </select>
                                                        ) : isProductDesc ? (
                                                            <input
                                                                type="text"
                                                                value={fieldValues[key] ?? ""}
                                                                onChange={e => handleFieldChange(key, e.target.value)}
                                                                placeholder="Auto-filled from product selection"
                                                                className="focus:outline-none h-9 px-2 rounded-lg border border-black border-opacity-25 text-sm text-slate-400"
                                                            />
                                                        ) : isRateField ? (
                                                            (() => {
                                                                // When monthly billing, show the monthly rate constant instead of annual
                                                                const rateMatch = key.match(/^RatePerYear(\d+)$/);
                                                                const monthlyKey = rateMatch ? `RatePerMonth${rateMatch[1]}` : null;
                                                                const displayValue = (billingCycle === "monthly" && monthlyKey)
                                                                    ? (fieldValues[monthlyKey] ?? "")
                                                                    : (fieldValues[key] ?? "");
                                                                const selectedCurrency = fieldValues["Currency"];
                                                                const rate = parseFloat(displayValue);
                                                                const previewRate = selectedCurrency && selectedCurrency !== "USD" ? currencyRates[selectedCurrency] : null;
                                                                const converted = (previewRate && !isNaN(rate)) ? (rate * previewRate * 1.05).toFixed(2) : null;
                                                                return (
                                                                    <>
                                                                        <input
                                                                            type="text"
                                                                            value={displayValue}
                                                                            readOnly
                                                                            placeholder="Auto-set by product"
                                                                            className="focus:outline-none h-9 px-2 rounded-lg border border-black border-opacity-25 text-sm bg-slate-50 text-slate-400 cursor-default"
                                                                        />
                                                                        {converted && <p className="text-xs text-slate-400 mt-0.5">{selectedCurrency} {converted}</p>}
                                                                    </>
                                                                );
                                                            })()
                                                        ) : (
                                                            <input
                                                                type="text"
                                                                value={fieldValues[key] ?? ""}
                                                                onChange={e => handleFieldChange(key, e.target.value)}
                                                                placeholder={key}
                                                                className="focus:outline-none h-9 px-2 rounded-lg border border-black border-opacity-25 text-sm"
                                                            />
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}

                                    {/* Add product button */}
                                    {productCount < MAX_PRODUCTS && (
                                        <button
                                            type="button"
                                            onClick={() => setProductCount(c => c + 1)}
                                            className="self-start flex items-center gap-x-1.5 text-xs text-blue-600 hover:text-blue-800 font-semibold"
                                        >
                                            <span className="text-lg leading-none">+</span> Add Product / Service
                                        </button>
                                    )}

                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col gap-y-3">
                                <div className="flex flex-col gap-y-1">
                                    <label className="text-sm text-slate-600">JSON object mapping template keys to values</label>
                                    <textarea
                                        className="border border-gray-300 rounded p-2 w-full font-mono text-sm"
                                        value={jsonText}
                                        onChange={e => setJsonText(e.target.value)}
                                        placeholder={'{"EstimateNumber": "001", "ClientBusinessName": "Acme Corp", ...}'}
                                        rows={10}
                                    />
                                </div>
                                <div className="flex flex-col gap-y-1">
                                    <label className="text-sm text-slate-600">.docx template</label>
                                    <input
                                        type="file"
                                        accept=".docx"
                                        onChange={handleFileChange}
                                        className="block w-full text-sm text-gray-500
                                          file:mr-4 file:py-2 file:px-4
                                          file:rounded-md file:border-0
                                          file:text-sm file:font-semibold
                                          file:bg-blue-50 file:text-blue-700
                                          hover:file:bg-blue-100
                                          cursor-pointer"
                                    />
                                    {adobeQuoteFile && (
                                        <p className="text-xs text-gray-500">Selected: {adobeQuoteFile.name}</p>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="flex flex-col">
                            <ThemeProvider theme={buttonTheme}>
                                <Button variant="contained" type="submit" color="primary" disabled={buttonDisabled}>
                                    Send Adobe Quote Test
                                </Button>
                            </ThemeProvider>
                        </div>
                    </form>
                )} {/* end activeTab quote/templates */}
            </div>
        </Modal>
    );
}